<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import Button from 'dashboard/components-next/button/Button.vue';
import ContactsForm from 'dashboard/components-next/Contacts/ContactsForm/ContactsForm.vue';
import ConfirmContactDeleteDialog from 'dashboard/components-next/Contacts/ContactsForm/ConfirmContactDeleteDialog.vue';
import Policy from 'dashboard/components/policy.vue';
import ContactAttributionCard from './ContactAttributionCard.vue';
import ContactAttributesCard from './ContactAttributesCard.vue';

const props = defineProps({
  contact: { type: Object, required: true },
});

const emit = defineEmits(['goToContactsList']);

// "Resumen" tab of the contact detail (CrmB): editable contact data card,
// attribution card, attributes and the delete banner (administrators only).
//
// El formulario pide los mismos cinco campos que el alta. Ciudad, país,
// biografía y redes no se piden ni se muestran; lo que un contacto ya
// tuviera guardado se conserva al actualizar, porque sólo se envían los
// campos que sí se editan.
const FORM_FIELDS = [
  'FIRST_NAME',
  'LAST_NAME',
  'PHONE_NUMBER',
  'EMAIL_ADDRESS',
  'COMPANY_NAME',
];

const { t } = useI18n();
const store = useStore();

const uiFlags = useMapGetter('contacts/getUIFlags');
const isUpdating = computed(() => uiFlags.value.isUpdating);

const contactsFormRef = ref(null);
const confirmDeleteContactDialogRef = ref(null);
const isFormInvalid = computed(() => contactsFormRef.value?.isFormInvalid);

// Local working copy: the form edits this object and "Actualizar contacto"
// sends it in one request.
const clone = value => JSON.parse(JSON.stringify(value || {}));
const contactData = ref(clone(props.contact));

watch(
  () => props.contact?.id,
  () => {
    contactData.value = clone(props.contact);
  }
);

const handleFormUpdate = updated => {
  const { additionalAttributes: updatedAttributes, ...rest } = updated;
  Object.assign(contactData.value, rest);
  contactData.value.additionalAttributes = {
    ...(contactData.value.additionalAttributes || {}),
    ...(updatedAttributes || {}),
  };
};

const updateContact = async () => {
  try {
    const { customAttributes, ...basicContactData } = contactData.value;
    await store.dispatch('contacts/update', basicContactData);
    await store.dispatch('contacts/fetchContactableInbox', props.contact.id);
    useAlert(t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.ERROR_MESSAGE'));
  }
};

const discardChanges = () => {
  contactData.value = clone(props.contact);
  contactsFormRef.value?.resetFromProps();
};

const openConfirmDeleteContactDialog = () => {
  confirmDeleteContactDialogRef.value?.dialogRef.open();
};
</script>

<template>
  <div class="flex flex-col gap-4">
    <section
      class="flex flex-col gap-3.5 px-5 py-[18px] rounded-xl border border-n-weak bg-n-solid-2"
    >
      <h3
        class="m-0 text-[13.5px] font-semibold tracking-tight text-n-slate-12"
      >
        {{ t('CONTACTS_LAYOUT.DETAILS.FORM_CARD.TITLE') }}
      </h3>
      <ContactsForm
        ref="contactsFormRef"
        :contact-data="contactData"
        is-details-view
        show-labels
        hide-titles
        hide-social-profiles
        :fields="FORM_FIELDS"
        @update="handleFormUpdate"
      />
      <div class="flex items-center gap-2">
        <Button
          :label="t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.UPDATE_BUTTON')"
          size="sm"
          :is-loading="isUpdating"
          :disabled="isUpdating || isFormInvalid"
          @click="updateContact"
        />
        <Button
          :label="t('CONTACTS_LAYOUT.DETAILS.FORM_CARD.DISCARD')"
          size="sm"
          color="slate"
          variant="outline"
          :disabled="isUpdating"
          @click="discardChanges"
        />
      </div>
    </section>

    <ContactAttributionCard :contact="contact" />

    <ContactAttributesCard :contact="contact" />

    <Policy :permissions="['administrator']">
      <section
        class="flex items-center gap-3.5 px-[18px] py-3.5 rounded-xl border border-n-ruby-5 bg-n-ruby-2"
      >
        <div class="flex flex-col gap-0.5 min-w-0">
          <span class="text-[12.5px] font-semibold text-n-ruby-11">
            {{ t('CONTACTS_LAYOUT.DETAILS.DELETE_CONTACT') }}
          </span>
          <span class="text-xs text-n-ruby-11/80">
            {{ t('CONTACTS_LAYOUT.DETAILS.DELETE_BANNER.DESCRIPTION') }}
          </span>
        </div>
        <Button
          :label="t('CONTACTS_LAYOUT.DETAILS.DELETE_BANNER.BUTTON')"
          color="ruby"
          variant="outline"
          size="sm"
          class="ml-auto flex-shrink-0"
          @click="openConfirmDeleteContactDialog"
        />
      </section>
      <ConfirmContactDeleteDialog
        ref="confirmDeleteContactDialogRef"
        :selected-contact="contact"
        @go-to-contacts-list="emit('goToContactsList')"
      />
    </Policy>
  </div>
</template>
